import { type ComponentPropsWithoutRef } from 'react';
import { Button, type ButtonProps } from '@zep-us/zus';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { cn } from '@/utils/tailwind.util';
import { trackGnbAnalytics } from './trackGnbAnalytics';
type HeaderButtonSize = NonNullable<ButtonProps['size']>;
export const EnterCodeButton = ({
  type,
  compact = false,
  className,
  textClassName,
  size
}: {
  type: 'drawer' | 'normal';
  compact?: boolean;
  className?: string;
  textClassName?: string;
  size?: HeaderButtonSize;
}) => {
  const {
    t
  } = useTranslation(['common']);
  const router = useRouter();
  const handleEnterCodeButtonClick = () => {
    void router.push('/join');
    trackGnbAnalytics('code', 'landing');
    logger.track('entercode_click');
  };
  const tEnterCode = t('gnb.header.enterCode');
  if (type === 'drawer') {
    return <Button className={cn('w-full', className)} size="lg" onClick={handleEnterCodeButtonClick}>
        <img src="/assets/icon_enter_white.svg" alt="entericon" />
        <span className={cn('min-w-0 truncate', textClassName)}>
          {tEnterCode}
        </span>
      </Button>;
  }
  const buttonSize = size ?? (compact ? 'sm' : 'md');
  return <Button size={buttonSize} intent={'alternative'} className={compact ? cn('h-[30px] min-h-[30px] rounded-xs px-xs text-caption-lg-semibold', className) : className} onClick={handleEnterCodeButtonClick} data-sentry-element="Button" data-sentry-component="EnterCodeButton" data-sentry-source-file="PageNavigatorButton.tsx">
      <span className={cn('min-w-0 truncate', textClassName)}>
        {tEnterCode}
      </span>
    </Button>;
};
export const _ExploreIconButton = () => {
  return <Button intent={'neutral'} className={'border-transparent bg-transparent'} size={'sm'} asChild data-sentry-element="Button" data-sentry-component="_ExploreIconButton" data-sentry-source-file="PageNavigatorButton.tsx">
      <Link prefetch={false} href={'/public'} onClick={() => trackGnbAnalytics('public', 'Header')} data-sentry-element="Link" data-sentry-source-file="PageNavigatorButton.tsx">
        <img src="/assets/icon_magnifying_glass.svg" alt="search" />
      </Link>
    </Button>;
};
const ExploreNormalButton = () => {
  const {
    t
  } = useTranslation(['common']);
  return <Button intent={'neutral'} asChild data-sentry-element="Button" data-sentry-component="ExploreNormalButton" data-sentry-source-file="PageNavigatorButton.tsx">
      <Link prefetch={false} href={'/public'} onClick={() => trackGnbAnalytics('public', 'Header')} data-sentry-element="Link" data-sentry-source-file="PageNavigatorButton.tsx">
        {t('common.header.explore')}
      </Link>
    </Button>;
};
export const MyQuizButtonDrawer = (props: ComponentPropsWithoutRef<'button'>) => {
  const {
    t
  } = useTranslation(['common']);
  return <Button variant="outlined" intent={'neutral'} className={'w-full'} size="lg" asChild {...props} data-sentry-element="Button" data-sentry-component="MyQuizButtonDrawer" data-sentry-source-file="PageNavigatorButton.tsx">
      <Link prefetch={false} href={'/myquiz'} onClick={() => {
      trackGnbAnalytics('mylist', 'Header');
      logger.track('click_my_profile');
    }} data-sentry-element="Link" data-sentry-source-file="PageNavigatorButton.tsx">
        {t('common:myQuiz')}
      </Link>
    </Button>;
};
export const ExploreDrawerButton = (props: ComponentPropsWithoutRef<'button'>) => {
  const {
    t
  } = useTranslation(['common']);
  return <Button asChild intent={'neutral'} className={cn('p-0 w-full justify-start')} {...props} data-sentry-element="Button" data-sentry-component="ExploreDrawerButton" data-sentry-source-file="PageNavigatorButton.tsx">
      <Link href={'/public'} onClick={() => trackGnbAnalytics('public', 'Header')} className="px-md py-[13.5px] text-[15px]" data-sentry-element="Link" data-sentry-source-file="PageNavigatorButton.tsx">
        {t('common.header.explore')}
      </Link>
    </Button>;
};
const MyQuizButtonNormal = () => {
  const {
    t
  } = useTranslation(['common']);
  return <Button asChild intent={'neutral'} className={cn('px-0 flex justify-start h-[36px] w-full  min-h-[36px]')} data-sentry-element="Button" data-sentry-component="MyQuizButtonNormal" data-sentry-source-file="PageNavigatorButton.tsx">
      <Link href={'/profile'} className="flex items-center justify-start gap-3xs px-2xs py-[10px]" onClick={() => trackGnbAnalytics('mylist', 'Header')} data-sentry-element="Link" data-sentry-source-file="PageNavigatorButton.tsx">
        <img src="/assets/icon_paper.svg" alt="myquiz" />
        {t('common:myProfile')}
      </Link>
    </Button>;
};
const _ExploreButton = () => <></>;
export const ExploreButton = Object.assign(_ExploreButton, {
  Drawer: ExploreDrawerButton,
  Normal: ExploreNormalButton,
  Icon: _ExploreIconButton
});
export const MyQuizButton = {
  Drawer: MyQuizButtonDrawer,
  Normal: MyQuizButtonNormal
};